/**
 * Lenis smooth scroll — Filo Fashion
 *
 * lenis.min.js and this file both use `defer` so execution order is guaranteed
 * by the browser: lenis.min.js (in <head>) always runs first, then this file.
 *
 * Scroll-lock mechanisms handled:
 *   1. html[scroll-lock] attribute  — dialog.js / <details scroll-lock> elements
 *   2. body.style.position = fixed  — DialogComponent.showDialog (cart, modals)
 *   3. filo-loader-pending class    — opening loader
 */
(function () {
  // Respect prefers-reduced-motion
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.Lenis) return;

  // ── Create Lenis instance ───────────────────────────────────────────────────

  const lenis = new Lenis({
    duration: 0.9,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo ease-out
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    syncTouch: false,
    wheelMultiplier: 0.9,
    touchMultiplier: 1,
    infinite: false,
    prevent: (node) => {
      return Boolean(
        node.closest?.(
          [
            '[data-lenis-prevent]',
            '[data-lenis-prevent-wheel]',
            'scroll-hint',
            'dialog',
            'details[open][scroll-lock]',
            '.cart-drawer',
            '.quick-add-modal',
            '.predictive-search',
            '.header__drawer',
          ].join(',')
        )
      );
    },
  });

  // Expose globally (scroll-to-top.js calls window.lenis.scrollTo)
  window.lenis = lenis;

  // ── RAF loop ────────────────────────────────────────────────────────────────

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // ── Prevent scroll hijacking inside carousels / horizontal sliders ──────────
  // scroll-hint is the theme's custom element wrapping all horizontal sliders.
  // Adding data-lenis-prevent tells Lenis to leave those elements alone.

  function markScrollHints() {
    document
      .querySelectorAll(
        [
          'scroll-hint:not([data-lenis-prevent])',
          '[scroll-lock]:not([data-lenis-prevent])',
          '.cart-drawer:not([data-lenis-prevent])',
          '.quick-add-modal:not([data-lenis-prevent])',
          '.predictive-search:not([data-lenis-prevent])',
        ].join(',')
      )
      .forEach((el) => {
        el.setAttribute('data-lenis-prevent', '');
      });
  }

  markScrollHints();

  // Also handle elements injected after initial load (quick-add, search…)
  new MutationObserver(markScrollHints).observe(document.body, {
    childList: true,
    subtree: true,
  });

  // ── Scroll-lock sync ───────────────────────────────────────────────────────

  const shouldStopLenis = () =>
    document.documentElement.hasAttribute('scroll-lock') ||
    document.documentElement.classList.contains('filo-loader-pending') ||
    document.body.style.position === 'fixed' ||
    Boolean(document.querySelector('dialog[open]'));

  function syncLenisState() {
    shouldStopLenis() ? lenis.stop() : lenis.start();
  }

  new MutationObserver(syncLenisState).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['scroll-lock', 'class'],
  });

  new MutationObserver(syncLenisState).observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
  });

  // Keep Lenis stopped for the whole time any modal/drawer dialog is open
  document.addEventListener('dialog:open', syncLenisState);
  document.addEventListener('dialog:close', () => {
    // Defer so dialog[open] is removed before we decide to restart
    requestAnimationFrame(syncLenisState);
  });

  window.addEventListener('load', () => lenis.resize(), { once: true });
  document.addEventListener('shopify:section:load', () => {
    markScrollHints();
    lenis.resize();
  });

  syncLenisState();
})();
